import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Twitter, Mail, Phone, MapPin } from 'lucide-react';
import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useEditMode } from '@/context/EditModeContext';
import SmartLink from '@/components/SmartLink';
import Modal from '@/components/editable/Modal';
import LinkListPanel from '@/components/editable/LinkListEditor';
import { DEFAULT_QUICK_LINKS } from '@/data/linkDefaults';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSiteData } from '@/context/SiteDataContext';
import EditText from '@/components/editable/EditText';
import EditImage from '@/components/editable/EditImage';

export default function Footer() {
  const { settings = {} } = useSiteData();
  const s = settings || {};
  const [email, setEmail] = useState('');
  const [editLinks, setEditLinks] = useState(false);
  const { editMode } = useEditMode();
  const quickLinks = (s.footer_links || DEFAULT_QUICK_LINKS).filter(l => l.visible !== false);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post('/newsletter', { email });
      toast.success('Subscribed! Watch your inbox for updates.');
      setEmail('');
    } catch {
      toast.error('Unable to subscribe. Try again.');
    }
  };

  const logo = s.logo_url || 'https://customer-assets-lqy194kg.emergentagent.net/job_first-ej/artifacts/mmnl3841_WIFI%20WEB%20LOGO.webp';
  const socials = [
    { key: 'facebook_url',  label: 'Facebook',  Icon: Facebook  },
    { key: 'instagram_url', label: 'Instagram', Icon: Instagram },
    { key: 'youtube_url',   label: 'YouTube',   Icon: Youtube   },
    { key: 'twitter_url',   label: 'Twitter',   Icon: Twitter   },
  ];

  return (
    <footer data-testid="site-footer" className="text-white pt-20 pb-8"
      style={{ backgroundColor: s.footer_bg_color || '#1F0F35' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <EditImage field="logo_url" target="settings" category="brand" accept="image/*">
              <img src={logo} alt="Wisconsin First Jurisdiction seal" className="w-14 h-14 object-contain" />
            </EditImage>
            <div>
              <EditText field="site_name" target="settings" as="div" className="font-serif text-xl font-bold">
                {s.site_name || 'Wisconsin First'}
              </EditText>
              <EditText field="site_subtitle" target="settings" as="div" className="text-[10px] tracking-[0.2em] uppercase text-gold">
                {s.site_subtitle || 'Ecclesiastical Jurisdiction · COGIC'}
              </EditText>
            </div>
          </div>
          <EditText field="footer_tagline" target="settings" as="p" multiline className="text-white/70 leading-relaxed max-w-md">
            {s.footer_tagline || 'Serving Wisconsin through Worship, Evangelism, Leadership, and Community — advancing the Kingdom of God across the state and beyond.'}
          </EditText>
          <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-md">
            <input
              data-testid="footer-newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-gold"
            />
            <button type="submit" data-testid="footer-newsletter-submit" className="btn-gold text-sm">Subscribe</button>
          </form>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-4">
            <EditText field="footer_links_heading" target="settings" as="h4" className="font-serif text-lg text-gold">{s.footer_links_heading || 'Quick Links'}</EditText>
            {editMode && (
              <button onClick={() => setEditLinks(true)} data-testid="edit-quick-links-btn" className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-gold text-royal-dark font-bold">
                <Pencil className="w-3 h-3" /> Edit links
              </button>
            )}
          </div>
          <ul className="space-y-2 text-white/70" data-testid="footer-quick-links">
            {quickLinks.map((l, i) => (
              <li key={i}><SmartLink href={l.href} className="link-hover" data-testid={`quick-link-${i}`}>{l.label}</SmartLink></li>
            ))}
            <li>
              {((s.give_href || '/giving').startsWith('http'))
                ? <a href={s.give_href} target="_blank" rel="noreferrer" className="link-hover text-gold">{s.give_label || 'Give'}</a>
                : <Link to={s.give_href || '/giving'} className="link-hover text-gold">{s.give_label || 'Give'}</Link>}
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-serif text-lg mb-4 text-gold">Jurisdictional Headquarters</h4>
          <ul className="space-y-3 text-white/70 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-1 text-gold shrink-0" />
              <span>
                <EditText field="address_line1" target="settings" as="span" className="block">{s.address_line1 || '2020 W. Fond du Lac Ave'}</EditText>
                <EditText field="address_line2" target="settings" as="span" className="block">{s.address_line2 || 'Milwaukee, WI 53206'}</EditText>
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gold shrink-0" />
              {s.phone
                ? <a href={`tel:${(s.phone || '').replace(/[^+0-9]/g, '')}`} className="hover:text-gold"><EditText field="phone" target="settings">{s.phone}</EditText></a>
                : <EditText field="phone" target="settings">{'(414) 555-0100'}</EditText>}
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gold shrink-0" />
              {s.email
                ? <a href={`mailto:${s.email}`} className="hover:text-gold"><EditText field="email" target="settings">{s.email}</EditText></a>
                : <EditText field="email" target="settings">{'info@wifirstjurisdiction.org'}</EditText>}
            </li>
          </ul>
          <div className="flex items-center gap-3 mt-5">
            {socials.map(({ key, label, Icon }) => {
              const href = s[key] && s[key] !== '#' ? s[key] : null;
              if (!href && !editMode) return null;
              const el = (
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold hover:text-royal-dark transition">
                  <Icon className="w-4 h-4" />
                </div>
              );
              return href
                ? <a key={key} href={href} target="_blank" rel="noreferrer" aria-label={label}>{el}</a>
                : <span key={key} aria-label={label} className="opacity-40">{el}</span>;
            })}
          </div>
        </div>
      </div>
      <div className="mt-16 border-t border-white/10 pt-6 text-center text-xs text-white/40 space-y-1" data-testid="footer-copyright">
        <div>
          © 2026 Wisconsin First, Church of God in Christ. All rights reserved. · <a href="https://wifirst.org/privacy" data-testid="footer-privacy-link" className="hover:text-gold">Privacy</a> · <a href="https://wifirst.org/terms" data-testid="footer-terms-link" className="hover:text-gold">Terms</a>
        </div>
        <div data-testid="footer-credit">The Micah White Projects, LLC.</div>
      </div>
      {editLinks && (
        <Modal title="Footer Quick Links" onClose={() => setEditLinks(false)} testid="quick-links-modal">
          <LinkListPanel settingsKey="footer_links" defaults={DEFAULT_QUICK_LINKS} testid="quick-links"
            help="Rename, reorder (drag), hide or remove links. Pages themselves are never deleted." onSaved={() => setEditLinks(false)} />
        </Modal>
      )}
    </footer>
  );
}
